import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { take } from 'rxjs/operators';
import { RollbarService } from '../../cross-team-shared/messaging/rollbar.service';
import { BrowserControlService } from '../../cross-team-shared/misc-services/browser-control.service';
import { DefaultAttributeService } from '../../outputs/annotation/annotation-shared/utils/default-attribute.service';
import { AbstractWorkspaceComponent } from '../../shared/abstract-workspace-component';
import { NavigationWarningService } from '../../shared/navigation/navigation-warning.service';
import { Step } from '../../team-apis/otterdam/step';
import { Task } from '../../team-apis/otterdam/task';
import { AnnotationChangeListenerService } from '../http/persistence/draft/annotation-change-listener.service';
import {
  BROADCAST_TYPE,
  BroadcastChannelService,
} from '../shared/misc-services/broadcast-channel.service';
import { TrainingTaskService } from '../shared/misc-services/training-task.service';
import { UndoRedoKeyboardShortcutService } from '../shared/misc-services/undo-redo-keyboard-shortcut.service';
import { workspaceProviders } from '../workspace-providers';
import { IdleTimeoutService } from './idle-timer/idle-timeout.service';
import { MultiTabsOpenedModalComponent } from './multi-tabs-opened-modal/multi-tabs-opened-modal.component';
import { DraftSubmissionService } from './submissions/draft-submission.service';
import { RequiredAnnotationsService } from './submissions/required-annotations.service';
import { SubmitTaskButtonService } from './submit-task-button/submit-task-button.service';
import { TimerService } from './timer-badge/timer.service';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- no loading state with on push
@Component({
  selector: 'h3-production-workspace',
  templateUrl: './production-workspace.component.html',
  styleUrls: [
    '../../shared/styles/workspace-styles.scss',
    './production-workspace.component.scss',
  ],
  providers: workspaceProviders,
  standalone: false,
})
export class ProductionWorkspaceComponent
  extends AbstractWorkspaceComponent
  implements OnInit
{
  // make sure all inputs are also in ProductionWorkspaceHostComponent
  @Input({ required: true }) projectId!: number;
  @Input({ required: true }) stepId!: number;
  @Input({ required: true }) qaGroupId!: string;
  @Input() templateId?: string;

  previousTask?: Task;
  task!: Task;
  hideAll$ = this.store('hasFatalError').$;
  isNavbarSubmitButtonEnabled = false;

  private annotationChangeListenerService = inject(
    AnnotationChangeListenerService,
  );
  private requiredAnnotationsService = inject(RequiredAnnotationsService);
  private trainingTaskService = inject(TrainingTaskService);
  private defaultAttributeService = inject(DefaultAttributeService);
  private submitTaskButtonService = inject(SubmitTaskButtonService);
  private undoRedoKeyboardShortcutService = inject(
    UndoRedoKeyboardShortcutService,
  );

  constructor(
    private browserControl: BrowserControlService,
    private draftSubmissionService: DraftSubmissionService,
    private timerService: TimerService,
    private idleTimeoutService: IdleTimeoutService,
    private broadcastChannelService: BroadcastChannelService,
    private dialogService: MatDialog,
    private rollbarService: RollbarService,
    private navigationWarningService: NavigationWarningService,
    injector: Injector,
  ) {
    super('production', injector);
    this.checkDictionaryErrors();
  }

  override async ngOnInit() {
    this.store('feedbackType').set('shape');
    await super.ngOnInit();
    // need to subscribe after task is loaded because the tracked event gets
    // task id
    this.setSubscriptionToTabBroadcast();
  }

  protected buildWorkspaceConfig() {
    return {
      name: this.name,
      projectId: this.projectId,
      qaGroupId: this.qaGroupId,
      stepId: this.stepId,
      templateId: this.templateId,
      user: this.currentUser,
    };
  }

  protected override async loadModels() {
    this.modelsLoading$.next('loading');
    await this.projectLoadingService.load();
    const [task] = await Promise.all([
      this.taskLoadingService.loadForProduction(),
      this.featureFlagLoadingService.load(),
      this.taxonomyService.load(),
    ]);
    this.settingsLoadingService.load();

    if (this.templateId) {
      this.setTask(task);
      this.updateFromStepInfo(this.store.state().project.steps[0]);
      if (this.store.isUsingObjects()) {
        this.requiredAnnotationsService.startListening();
        this.defaultAttributeService.startListening();
      }
      return;
    }

    this.setStepFromStepId();
    await this.setTaskAndLoadTaskModels(task, task._extras.previous_task);

    if (!this.store.isUsingObjects()) {
      // We need to register this after the task has been set to avoid errors
      this.navigationWarningService.register(
        () => this.draftSubmissionService.haveAnswersChanged(),
        this,
      );
    }
  }

  protected override handleModelsLoaded() {
    this.isNavbarSubmitButtonEnabled =
      this.submitTaskButtonService.isNavbarSubmitButtonEnabled();

    if (this.templateId) {
      this.modelsLoading$.next('loaded');
      this.annotationTransientInfoService.subscribeToChangesUpdatingTransientInfo();
      return;
    }

    super.handleModelsLoaded();
    this.draftSubmissionService.initialize(this.stepId);
    this.startTimer(this.store.getStep());
    this.idleTimeoutService.start();
    this.store('lastInteractiveEventTimestamp').set(performance.now());
    this.handleModelsLoadedForObjects();
  }

  private handleModelsLoadedForObjects() {
    if (this.store.isUsingObjects()) {
      this.annotationChangeListenerService.startListening();
      this.requiredAnnotationsService.startListening();
      this.defaultAttributeService.startListening();
      this.undoRedoKeyboardShortcutService.register();
    }
  }

  private setTask(task: Task, previousTask?: Task) {
    if (previousTask && !this.store.isUsingObjects()) {
      previousTask.answers_or_gold = this.store.extractAnswersFromOutputInfo();
    }

    this.task = task;
    this.previousTask = previousTask;
    this.store.batch(() => {
      this.store('lastSavedDraftTimestamp').delete();
      this.store.setTask(task);
    });

    this.browserControl.preload(task._extras.urls_to_preload);
  }

  private startTimer(step: Step) {
    if (this.store.isUsingObjects()) {
      return;
    }
    this.timerService.start(step.target_duration, {
      savedDuration: this.task.duration,
      isVisible: step.target_duration_enabled,
    });
  }

  private async setTaskAndLoadTaskModels(task: Task, previousTask?: Task) {
    this.setTask(task, previousTask);

    await Promise.all([
      this.annotationLoadingService.load(task),
      this.annotationLoadingService.loadShapesForTraining(task),
      this.feedbackLoadingService.load(),
      this.lastRoundFeedbackLoadingService.load(task),
      this.trainingTaskService.loadLatestScore(task),
      this.loadPreAnnotationsIfAllowed(),
    ]);
  }

  private checkDictionaryErrors() {
    if ((globalThis as any).dictionaryIdsInError?.length) {
      this.rollbarService.error(
        'Error loading dictionaries',
        (globalThis as any).dictionaryIdsInError,
      );
      this.messagingService.error(
        'Error loading dictionary. Please reload this page.',
      );
      this.store('hasFatalError').set(true);
    }
  }

  private setSubscriptionToTabBroadcast() {
    this.broadcastChannelService.notifyWorkspaceOpened();

    this.subscribeTo(
      this.broadcastChannelService
        .getLocationByType$(BROADCAST_TYPE.WorkspaceOpened)
        .pipe(filter((payload) => payload === window.location.href)),
      () => {
        this.broadcastChannelService.postMessage({
          type: BROADCAST_TYPE.SameWorkspaceDetected,
          location: window.location.href,
        });
      },
    );
    this.setUpSubscriptionForMultiTabsOpenedModel();
  }

  private setUpSubscriptionForMultiTabsOpenedModel() {
    this.subscribeTo(
      this.broadcastChannelService
        .getLocationByType$(BROADCAST_TYPE.SameWorkspaceDetected)
        .pipe(
          filter((payload) => payload === window.location.href),
          take(1),
        ),
      () => {
        this.dialogService.open(MultiTabsOpenedModalComponent, {
          disableClose: true,
          // for some reason, need to pass the injector for the component to
          // have access to TaskWorkspaceStore
          injector: this.injector,
        });
        this.eventTrackingService.track({
          id: 'multiple_production_workspaces',
          bucket: 'untracked',
          action: 'page_open',
        });
      },
    );
  }
}
